import React, { useState, useEffect, useRef } from 'react';
import './Blog.css';

const Blog = () => {
    const [blogs, setBlogs] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [blogsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const [isOwner, setIsOwner] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [loginDetails, setLoginDetails] = useState({ username: '', password: '' });
    const [newBlog, setNewBlog] = useState({ title: '', content: '', image: null, author: '' });
    const contentRef = useRef(null);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch('/api/blogs'); // Updated endpoint
                if (!response.ok) {
                    throw new Error('Failed to fetch blogs');
                }
                const data = await response.json();
                setBlogs(data);
                setFilteredBlogs(data);
            } catch (err) {
                console.error('Error fetching blogs:', err);
            }
        };

        fetchBlogs();
    }, []);

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        setFilteredBlogs(
            blogs.filter((blog) =>
                blog.title.toLowerCase().includes(query) || blog.content.toLowerCase().includes(query)
            )
        );
        setCurrentPage(1); // Reset to the first page after a search
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/login', { // Updated endpoint
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginDetails),
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(`Login failed: ${errorData.message}`);
                return;
            }

            const data = await response.json();
            if (data.success) {
                setIsOwner(true);
                setShowLogin(false);
                alert('Login successful!');
            } else {
                alert('Invalid credentials. Please try again.');
            }
        } catch (err) {
            console.error('Error logging in:', err);
            alert('An error occurred while logging in. Please try again.');
        }
    };

    const handlePostBlog = async (e) => {
        e.preventDefault();
        const content = contentRef.current.innerHTML; // Get the formatted content
        const formData = new FormData();
        formData.append('title', newBlog.title);
        formData.append('content', content);
        formData.append('author', newBlog.author);
        if (newBlog.image) {
            formData.append('image', newBlog.image);
        }

        try {
            const response = await fetch('/api/blogs', { // Updated endpoint
                method: 'POST',
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setBlogs([...blogs, data.blog]);
                setFilteredBlogs([...blogs, data.blog]);
                setNewBlog({ title: '', content: '', image: null, author: '' });
                contentRef.current.innerHTML = '';
                alert('Blog posted successfully!');
            }
        } catch (err) {
            console.error('Error posting blog:', err);
        }
    };

    const handleDeleteBlog = async (id) => {
        try {
            const response = await fetch(`/api/blogs/${id}`, { // Updated endpoint
                method: 'DELETE',
            });
            if (response.ok) {
                const updatedBlogs = blogs.filter((blog) => blog.id !== id);
                setBlogs(updatedBlogs);
                setFilteredBlogs(updatedBlogs);
                alert('Blog deleted successfully!');
            } else {
                alert('Failed to delete blog. Please try again.');
            }
        } catch (err) {
            console.error('Error deleting blog:', err);
        }
    };

    // Pagination logic
    const indexOfLastBlog = currentPage * blogsPerPage;
    const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
    const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="blog-container">
            {/* Owner Login/Logout Button */}
            <div className="login-section">
                {isOwner ? (
                    <button onClick={() => setIsOwner(false)} className="login-toggle-button">
                        Logout
                    </button>
                ) : (
                    <button onClick={() => setShowLogin(!showLogin)} className="login-toggle-button">
                        Owner Login
                    </button>
                )}
                {showLogin && !isOwner && (
                    <form className="login-form" onSubmit={handleLogin}>
                        <input
                            type="text"
                            placeholder="Username"
                            value={loginDetails.username}
                            onChange={(e) => setLoginDetails({ ...loginDetails, username: e.target.value })}
                            required
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={loginDetails.password}
                            onChange={(e) => setLoginDetails({ ...loginDetails, password: e.target.value })}
                            required
                        />
                        <button type="submit">Login</button>
                    </form>
                )}
            </div>

            {/* Blog Search */}
            <h1>Our Health Blog</h1>
            <p className="blog-description">
                This blog is intended to help educate and inform you about how you can live a healthier life. We also post fun new services we provide! Explore our posts to learn more about physical therapy, wellness, and recovery.
            </p>
            <input
                type="text"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={handleSearch}
                className="blog-search"
            />

            {/* Blog Content */}
            <div className="blogs">
                {currentBlogs.map((blog) => (
                    <div key={blog.id} className="accordion-item">
                        <h3
                            className="accordion-title"
                            onClick={() => {
                                const element = document.getElementById(`blog-${blog.id}`);
                                element.style.display =
                                    element.style.display === 'block' ? 'none' : 'block';
                            }}
                        >
                            {blog.title}
                        </h3>
                        <div id={`blog-${blog.id}`} className="accordion-content" style={{ display: 'none' }}>
                            {blog.image_url && (
                                <img
                                    src={`http://localhost:5000${blog.image_url}`}
                                    alt={blog.title}
                                    className="blog-image"
                                />
                            )}
                            <p><strong>Author:</strong> {blog.author}</p>
                            <p><strong>Date:</strong> {new Date(blog.created_at).toLocaleDateString()}</p>
                            <p dangerouslySetInnerHTML={{ __html: blog.content }}></p>
                            {isOwner && (
                                <button onClick={() => handleDeleteBlog(blog.id)} className="delete-blog-button">
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            <div className="pagination">
                {Array.from({ length: Math.ceil(filteredBlogs.length / blogsPerPage) }, (_, i) => (
                    <button
                        key={i}
                        onClick={() => paginate(i + 1)}
                        className={currentPage === i + 1 ? 'active' : ''}
                    >
                        {i + 1}
                    </button>
                ))}
            </div>

            {/* Post Blog Form */}
            {isOwner && (
                <form className="post-blog-form" onSubmit={handlePostBlog}>
                    <h2>Post a New Blog</h2>
                    <input
                        type="text"
                        placeholder="Blog Title"
                        value={newBlog.title}
                        onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Author"
                        value={newBlog.author}
                        onChange={(e) => setNewBlog({ ...newBlog, author: e.target.value })}
                        required
                    />
                    <div
                        ref={contentRef}
                        contentEditable
                        className="rich-text-editor"
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            padding: '10px',
                            minHeight: '150px',
                            fontSize: '16px',
                            overflowY: 'auto',
                        }}
                    ></div>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setNewBlog({ ...newBlog, image: e.target.files[0] })}
                    />
                    <button type="submit">Post Blog</button>
                </form>
            )}
        </div>
    );
};

export default Blog;
